/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import Input from '~/components/Input';
import InputCheckbox from '~/components/InputCheckbox';
import InputMask from '~/components/InputMask';
import Select from '~/components/Select';
import { useAuth } from '~/hooks/Auth';

import api from '~/services/api';
import { ITicket } from '..';
import { IQuestion } from '~/pages/Ticket';
import Question from '~/components/Question';
import InputRadio from '~/components/InputRadio';
import formatDate from '~/utils/formatDate';

export interface IAnswer {
  event_id: number;
  question_type_id: number;
  question_id: number;
  alternative_id: number;
  text: string;
}

interface IAlternativesResponse {
  id: number;
  name: string;
}

export interface IQuestionResponse {
  id: number;
  event_id: number;
  question_type_id: number;
  name: string;
  order: number;
  required?: boolean;
  alternatives: IAlternativesResponse[];
}

interface ITicketProps {
  ticket: ITicket;
  myTicketIndex: number;
  onChangeMyTicket: (ticket: ITicket) => void;
  onChangeTicket: (ticket: ITicket) => void;
  onChangeAnswers: (answers: IAnswer[]) => void;
  onChangeSize?: (ticketIndex: number, sizeId: number) => void;
  eventId?: number;
  ticketError?: string;
}

// subir

const Ticket: React.FC<ITicketProps> = ({
  ticket: ticketData,
  onChangeMyTicket,
  onChangeTicket,
  onChangeAnswers,
  onChangeSize,
  eventId,
  ticketError,
}) => {
  const { user } = useAuth();

  const [ticket, setTicket] = useState({} as ITicket);
  const [questions, setQuestions] = useState<IQuestion[]>([]);
  const [answers, setAnswers] = useState<IAnswer[]>([]);

  const genderOptions = [
    { id: 'M', value: 'Masculino' },
    { id: 'F', value: 'Feminino' },
  ];

  const selectedGender = genderOptions.find((opt) => opt.id === ticket.gender);

  useEffect(() => {
    api
      .get<IQuestionResponse[]>(`questions/events/${eventId}`)
      .then((response) => {
        const data = response.data.map<IQuestion>((question) => {
          let type = 'free-text';

          if (question.question_type_id === 2) {
            type = 'unique-choice';
          }

          if (question.question_type_id === 3) {
            type = 'multiple-choice';
          }

          const alternatives = question.alternatives.map((alternative) => ({
            id: alternative.id,
            text: alternative.name,
            new: false,
          }));

          return {
            id: question.id,
            question: question.name,
            type: type as IQuestion['type'],
            order: question.order,
            alternatives,
            isRequired: true,
          };
        });
        setQuestions(data);
      });
  }, [eventId]);

  useEffect(() => {
    setTicket(ticketData);
  }, [ticketData]);

  const handleChangeDocument = useCallback(
    async (e) => {
      const { value } = e.target;
      const newTicket = { ...ticket };
      if (value.length === 14) {
        const response = await api.get(`tickets/document/${value}`);
        if (response.data.length > 0) {
          newTicket.name = response.data[0].name;
          newTicket.document = value;
          newTicket.phone = response.data[0].phone;
          newTicket.email = response.data[0].email;
          newTicket.birthdate = response.data[0].birthdate
            ? formatDate(response.data[0].birthdate, 'dd/MM/yyyy')
            : '';
          newTicket.gender = response.data[0].gender || '';
        } else {
          const responseAdditionalInformation = await api.get(
            `additional-informations/document/${value}`
          );
          if (responseAdditionalInformation.data) {
            newTicket.name = responseAdditionalInformation.data.user.name;
            newTicket.phone = responseAdditionalInformation.data.phone;
            newTicket.email = responseAdditionalInformation.data.user.email;
            newTicket.birthdate = responseAdditionalInformation.data.birthdate
              ? formatDate(
                  responseAdditionalInformation.data.birthdate,
                  'dd/MM/yyyy'
                )
              : '';
            newTicket.gender = responseAdditionalInformation.data.gender || '';
            newTicket.document = value;
          } else {
            newTicket.name = newTicket.name || '';
            newTicket.phone = newTicket.phone || '';
            newTicket.email = newTicket.email || '';
            newTicket.birthdate = newTicket.birthdate || '';
            newTicket.gender = newTicket.gender || '';
            newTicket.document = value;
          }
        }
      } else {
        newTicket.name = newTicket.name || '';
        newTicket.phone = newTicket.phone || '';
        newTicket.email = newTicket.email || '';
        newTicket.birthdate = newTicket.birthdate || '';
        newTicket.gender = newTicket.gender || '';
        newTicket.document = value;
      }
      onChangeTicket(newTicket);
    },
    [onChangeTicket, ticket]
  );

  const handleChangeName = useCallback(
    (e) => {
      const { value } = e.target;
      const newTicket = { ...ticket };
      newTicket.name = value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s]/g, '');
      onChangeTicket(newTicket);
    },
    [onChangeTicket, ticket]
  );

  const handleChangePhone = useCallback(
    (e) => {
      const { value } = e.target;
      const newTicket = { ...ticket };
      newTicket.phone = value;
      onChangeTicket(newTicket);
    },
    [onChangeTicket, ticket]
  );

  const handleChangeEmail = useCallback(
    (e) => {
      const { value } = e.target;
      const newTicket = { ...ticket };
      newTicket.email = value;
      onChangeTicket(newTicket);
    },
    [onChangeTicket, ticket]
  );

  const handleChangeBirthdate = useCallback(
    (e) => {
      const { value } = e.target;
      const newTicket = { ...ticket };
      newTicket.birthdate = value;
      onChangeTicket(newTicket);
    },
    [onChangeTicket, ticket]
  );

  const handleChangeAnswer = useCallback(
    (answersData, questionId) => {
      const answerExist = answers.some(
        (answer) => answer.question_id === questionId
      );

      let dataAnswers: IAnswer[] = answers;
      if (answerExist) {
        dataAnswers = dataAnswers.filter(
          (answer) => answer.question_id !== questionId
        );

        dataAnswers.push(...answersData);
      } else {
        dataAnswers.push(...answersData);
      }
      onChangeAnswers(dataAnswers);
      setAnswers(dataAnswers);
    },
    [answers, onChangeAnswers]
  );

  return (
    <>
      {Object.keys(ticket).length > 0 && (
        <div
          key={ticket.item.batch_category_id}
          className="border-ticket p-3 mb-3"
        >
          <h3 className="h5 text-black">
            Ingresso #{(ticket.index + 1).toString().padStart(4, '0')}
          </h3>
          <p className="pb-3 border-bottom">
            Modalidade: {ticket.item.modality} | Categoria:{' '}
            {ticket.item.category}
          </p>
          {ticket.showMyTicket && (
            <InputCheckbox
              type="checkbox"
              name={`my_ticket_${ticket.index}`}
              className="my-3 d-block"
              options={[
                {
                  label: 'Este ingresso é o meu',
                  value: 'Este ingresso é o meu',
                  selected: ticket.myTicket,
                },
              ]}
              onChange={() => onChangeMyTicket(ticket)}
              error={ticketError}
            />
          )}
          <label
            className={`${
              !ticket.myTicket || !user.additionalInformation.document
                ? 'w-100 mb-3'
                : 'd-none'
            }`}
          >
            <span className="fw-normal d-block mb-2">Documento*</span>
            <div className="d-flex">
              <Select
                className="bd-input w-25"
                name={`documents_${ticket.index}`}
                readOnly
                options={[
                  {
                    value: 'CPF',
                    id: 0,
                    selected: true,
                  },
                ]}
              />
              <div className="col mt-0 ms-1">
                <InputMask
                  kind="cpf"
                  name={`document_${ticket.index}`}
                  className="bd-input"
                  onChange={handleChangeDocument}
                  value={ticket.document}
                />
              </div>
            </div>
          </label>
          <label
            className={`${
              !ticket.myTicket || !user.name ? 'w-100 mb-3' : 'd-none'
            }`}
          >
            <span className="fw-normal d-block mb-2">Nome Completo*</span>
            <Input
              name={`name_${ticket.index}`}
              className="bd-input"
              onChange={handleChangeName}
              value={ticket.name}
            />
          </label>
          <label
            className={`${
              !ticket.myTicket || !user.additionalInformation.birthdate
                ? 'w-100 mb-3'
                : 'd-none'
            }`}
          >
            <span className="fw-normal d-block mb-2">Data de nascimento</span>
            <InputMask
              kind="custom"
              options={{ mask: '99/99/9999' }}
              name={`birthdate_${ticket.index}`}
              className="bd-input"
              value={ticket.birthdate}
              inputMode="numeric"
              onChange={handleChangeBirthdate}
            />
          </label>
          <label
            className={`${
              !ticket.myTicket || !user.additionalInformation.gender
                ? 'w-100 mb-3'
                : 'd-none'
            }`}
          >
            <span className="fw-normal d-block mb-2">Gênero</span>
            <InputRadio
              name={`gender_${ticket.index}`}
              className="d-flex justify-content-start check"
              options={genderOptions}
              selected={selectedGender}
              onChange={(option) => {
                const newTicket = {
                  ...ticket,
                  gender: option.id as string | undefined,
                };
                onChangeTicket(newTicket);
              }}
            />
          </label>
          <label
            className={`${
              !ticket.myTicket || !user.additionalInformation.phone
                ? 'w-100 mb-3'
                : 'd-none'
            }`}
          >
            <span className="fw-normal d-block mb-2">Telefone*</span>
            <InputMask
              kind="cel-phone"
              name={`phone_${ticket.index}`}
              className="bd-input"
              onChange={handleChangePhone}
              value={ticket.phone}
            />
          </label>
          <label
            className={`${
              !ticket.myTicket || !user.email ? 'w-100 mb-3' : 'd-none'
            }`}
          >
            <span className="fw-normal d-block mb-2 size">E-mail*</span>
            <Input
              type="email"
              name={`email_${ticket.index}`}
              className="bd-input"
              onChange={handleChangeEmail}
              value={ticket.email}
            />
          </label>
          {ticket.hasKit && ticketData.sizes && ticketData.sizes.length > 0 && (
            <label className="mb-3 w-100">
              <span className="fw-normal d-block mb-2">Tamanho do(a) kit*</span>
              <Select
                className="bd-input select-tam"
                name={`size_${ticket.index}`}
                options={ticketData.sizes
                  .filter((quantity) => !quantity.notSelectable)
                  .map((quantity) => ({
                    ...quantity,
                    value: quantity.value.replaceAll('_BABY', ' BABYLOOK'),
                  }))}
                onChange={(option: any) => {
                  if (onChangeSize) {
                    onChangeSize(ticket.index, option.id as number);
                  }
                }}
              />
            </label>
          )}
          {Object.keys(questions).length > 0 && (
            <div className="bg-information">
              <h3 className="h6">Mais informações</h3>
              {questions.map((question) => (
                <Question
                  key={question.id}
                  question={question}
                  onChangeAnswer={(answersData) =>
                    handleChangeAnswer(answersData, question.id)
                  }
                  ticketIndex={ticket.index}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Ticket;
